import { FC } from 'react';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import useRegisterUser from '@/api/useRegisterUser.ts';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import { passwordSchema } from '$/utils/validators/passwordSchema.ts';
import { TextField, Typography, Box, Stack } from '@mui/material';

type Schema = SignUpStepSchema;

const steps = ['sign-up', 'success-sign-up'] as const;
const RegisterPage: FC = () => {
  return (
    <Stepper.Wrapper steps={steps} initialStep="sign-up">
      <Stepper.Step<typeof steps> step="sign-up">
        <StepSignUp />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="success-sign-up">
        <StepSuccessSignUp />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default RegisterPage;

const signUpStepSchema = z
  .object({
    name: z.string().min(1, { message: 'This field is required.' }),
    email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: 'This field is required.' }),
    terms: z.boolean().refine((value) => value, { message: 'You must agree to the terms and conditions' }),
    user_type: z.enum(['admin', 'user']).default('user'),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'The passwords did not match',
        path: ['repeat_password'],
      });
    }
  });

type SignUpStepSchema = z.infer<typeof signUpStepSchema>;
function StepSignUp() {
  const { t } = useTranslation();
  const {
    mutateAsync: registerUser,
    isError: isErrorRegisterUser,
    isPending: isLoadingRegisterUser,
    error: errorRegisterUser,
  } = useRegisterUser();
  const { goToStep, setContextData } = useStepperContext<typeof steps, Schema>();
  const { control, handleSubmit } = useForm<SignUpStepSchema>({
    resolver: zodResolver(signUpStepSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      repeat_password: '',
      terms: false,
      user_type: 'user',
    },
  });

  const handleNextStep = (data: SignUpStepSchema) => {
    void (async () => {
      try {
        if (isLoadingRegisterUser) return;
        await registerUser(data);
        setContextData(data);
        goToStep('success-sign-up');
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent className="w-screen h-screen bg-bg-light px-4 py-6 pt-16">
      <Stack spacing={4} sx={{ maxWidth: '100%' }}>  
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Create Student Account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter your information to create your student account.
          </Typography>
        </Box>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(handleNextStep)}>
          <Stack spacing={3}>
            {isErrorRegisterUser && <Alert status="error">{errorRegisterUser.message}</Alert>}
            <Controller
              control={control}
              name="name"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  value={value}
                  onChange={onChange}
                  placeholder={t('placeholder.name', { defaultValue: 'Your name' })}
                  variant="outlined"
                />
              )}
            />

            <Controller
              control={control}
              name="email"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  value={value}
                  onChange={onChange}
                  placeholder={t('placeholder.email')}
                  variant="outlined"
                />
              )}
            />
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  value={value}
                  onChange={onChange}
                  type="password"
                  autoComplete="password"
                  placeholder={t('placeholder.password')}
                  variant="outlined"
                />
              )}
            />
            <Controller
              control={control}
              name="repeat_password"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.repeat_password}
                  helperText={errors.repeat_password?.message}
                  value={value}
                  onChange={onChange}
                  type="password"
                  autoComplete="repeat_password"
                  placeholder={t('placeholder.repeat_password')}
                  variant="outlined"
                />
              )}
            />
            <Controller
              control={control}
              name="terms"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={onChange}
                    className="checkbox checkbox-primary"
                  />
                  <Typography 
                    variant="body2" 
                    color={errors.terms ? "error" : "text.primary"}
                    sx={{ '& a': { color: 'primary.main', textDecoration: 'underline' } }}
                  >
                    <Trans i18nKey="registrationPage.agreeToTerms">
                      I accept the
                      <Link target="_blank" to={import.meta.env.VITE_TERMS_LINK}>
                        Terms and Conditions
                      </Link>
                    </Trans>
                  </Typography>
                </Box>
              )}
            />
            <Button 
              color="primary"
              className="btn-primary w-full rounded-full normal-case bg-[#4E7DF6] hover:bg-[#3D63C4] border-none"
              type="submit"
              disabled={isLoadingRegisterUser}
            >
              {t('button.createAccount')}
            </Button>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Link to={Routes.LOGIN}>
              <Button color="ghost">{t('button.loginToExistingAccount')}</Button>
            </Link>
          </Box>
        </form>
      </Stack>
    </BrandCardSideContent>
  );
}

function StepSuccessSignUp() {
  const { t } = useTranslation();
  const { contextData } = useStepperContext<typeof steps, Schema>();

  return (
    <BrandCardSideContent>
      <div className="flex flex-col justify-center items-center gap-8 w-[343px] max-w-full">
        <div className="flex flex-col gap-4 items-center text-center">
          <h2 className="font-medium text-5xl">{t('registrationPage.congrats')}</h2>
          <p className="text-text-secondary">{t('registrationPage.congratsText')}</p>
        </div>
        <Link
          className="w-full flex flex-col"
          to={passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
            email: encodeURIComponent(contextData?.email as string),
          })}
        >
          <Button color="primary" className="w-full rounded-full normal-case bg-[#4E7DF6] hover:bg-[#3D63C4] border-none">
            {t('button.gotIt')}
          </Button>
        </Link>
      </div>
    </BrandCardSideContent>
  );
}
